import React, { useState } from "react";
import { faExclamationTriangle, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Tooltip, Stack } from "@mui/material";
import { FormattedMessage } from "react-intl";
import { useFetch } from "./FetchProvider";
import { useRevalidator } from "react-router-dom";
import { useNotification } from "./NotificationProvider";
import { useUserData } from "../context/UserDataProvider";

const EmailVerificationInfo: React.FC<{ data: any }> = ({ data }) => {
    const [resendInProgress, setResendInProgress] = useState(false);
    const [resendResult, setResendResult] = useState<boolean>(null);
    const fetch = useFetch();
    const revalidator = useRevalidator();
    const { showSuccess, showError } = useNotification();
    const userData = useUserData();

    let verificationToken = null;
    for (const uet of data.tokens) {
        if (uet.type === "REGISTRATION") {
            // still waiting on the invitation, the client resends that
            return <span />;
        }
        if (uet.type === "VERIFICATION") {
            verificationToken = uet;
        }
    }
    // without a token its link expired and was cleaned up, sending creates a new one
    const isWarn = resendResult == null ? !verificationToken?.sent : !resendResult;

    const verifyEmail = async (userEmailTokenId: number) => {
        const response = await fetch("/api/verify-email-by-id", {
            method: "POST",
            body: JSON.stringify({ id: userEmailTokenId }),
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
            },
        });

        if (response.ok) {
            showSuccess(
                <FormattedMessage id="user_email.email_verified" defaultMessage="Email verified successfully" />,
                <FormattedMessage id="user_email.success" defaultMessage="Success" />,
            );
            revalidator.revalidate();
        } else {
            const result = await response.json();
            showError(result.reason, <FormattedMessage id="user_email.error" defaultMessage="Error" />);
        }
    };

    const resendEmailVerification = async (userEmailId: number) => {
        setResendInProgress(true);

        const response = await fetch("/api/resend-email-verification", {
            method: "POST",
            body: JSON.stringify({ user_email_id: userEmailId }),
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
            },
        });

        if (response.ok) {
            showSuccess(
                <FormattedMessage id="user_email.email_has_been_sent" defaultMessage="Email has been sent" />,
                <FormattedMessage id="user_email.success" defaultMessage="Success" />,
            );
            setResendResult(true);
            setResendInProgress(false);
            if (verificationToken == null) revalidator.revalidate();
        } else {
            const result = await response.json();
            showError(result.reason, <FormattedMessage id="user_email.error" defaultMessage="Error" />);
            setResendResult(false);
            setResendInProgress(false);
        }
    };

    let feedbackLabel = <FormattedMessage id="user_email.confirm_sent" defaultMessage="Verification email sent." />;
    if (verificationToken == null && resendResult == null)
        feedbackLabel = (
            <FormattedMessage
                id="user_email.verification_expired"
                defaultMessage="The verification link has expired, send a new one."
            />
        );
    else if (isWarn)
        feedbackLabel = (
            <FormattedMessage
                id="user_email.no_verif_sent"
                defaultMessage="Due some SMTP server error previous attempt to send email was failed."
            />
        );

    let resendLabel = <FormattedMessage id="user_email.resend" defaultMessage="Resend" />;
    if (verificationToken == null && resendResult == null)
        resendLabel = <FormattedMessage id="user_email.send_verification" defaultMessage="Send verification" />;
    else if (isWarn) resendLabel = <FormattedMessage id="user_email.retry" defaultMessage="Retry" />;

    // the raw token is never served by the API, so the operator verifies the record itself
    let verifyBtn = null;
    if (verificationToken != null && userData?.hasPermission("system"))
        verifyBtn = (
            <Button
                data-action="email-verify"
                onClick={() => {
                    verifyEmail(verificationToken.id);
                }}
                variant="contained"
                size="small"
            >
                <FormattedMessage id="user_email.verify" defaultMessage="Verify email" />
            </Button>
        );

    return (
        <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title={feedbackLabel} placement="bottom">
                <FontAwesomeIcon
                    icon={isWarn ? faExclamationTriangle : faInfoCircle}
                    color={isWarn ? "orange" : "green"}
                    style={{ margin: "0 10px" }}
                />
            </Tooltip>
            <Button
                variant="contained"
                size="small"
                disabled={resendInProgress}
                onClick={() => {
                    resendEmailVerification(data.id);
                }}
            >
                {resendLabel}
            </Button>
            {verifyBtn}
        </Stack>
    );
};

export default EmailVerificationInfo;
